// src/services/onboardingService.js
import axios from 'axios';
import { API } from '../utils/apiUrls';

/**
 * Merchant Onboarding Service
 * Handles all API calls for the self-signup merchant onboarding flow
 */

// Create axios instances for different base URLs
const createIAMClient = () => {
  return axios.create({
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

const createPocasClient = (token = null) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = token; // No Bearer prefix as per requirements
  }
  return axios.create({
    timeout: 30000,
    headers,
  });
};

const createOMSClient = (token = null) => {
  const headers = {};
  if (token) {
    headers['Authorization'] = token;
  }
  return axios.create({
    timeout: 30000,
    headers,
  });
};

/**
 * Step 1: Register a new merchant user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} Registration response
 */
export const registerUser = async (userData) => {
  try {
    const client = createIAMClient();

    // Either organization_name or brand_name must be provided.
    // If only one is provided, copy it into the other so both fields are populated.
    const organizationName = (userData.organization_name || '').trim();
    const brandName = (userData.brand_name || '').trim();
    const resolvedOrganizationName = organizationName || brandName;
    const resolvedBrandName = brandName || organizationName;

    const response = await client.post(API.iamRegister(), {
      organization_name: resolvedOrganizationName,
      brand_name: resolvedBrandName,
      platform_id: userData.platform_id ?? 1,
      is_standard_user: userData.is_standard_user ?? true,
      username: userData.username,
      password: userData.password,
      email: userData.email,
      role_id: userData.role_id ?? 1, // Merchant role
      firstname: userData.firstname,
      lastname: userData.lastname,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Registration failed',
    };
  }
};

/**
 * Step 2: Authenticate and get auth token
 * @param {Object} credentials - Object containing username and password
 * @param {string} credentials.username
 * @param {string} credentials.password
 * @returns {Promise<string>} Auth token string
 */
export const authenticateUser = async ({ username, password }) => {
  try {
    const client = createIAMClient();
    
    console.log('🔐 Calling auth API with:', { username });
    
    const response = await client.post(API.iamAuth(), {
      username,
      password,
    });

    console.log('✅ Auth API response received:', response.data);

    // Extract token - API returns either { auth_token: "..." } or just the token string
    const token = response.data?.auth_token || response.data;
    
    console.log('📝 Token type:', typeof token);
    console.log('📝 Token value:', token);
    
    if (!token || typeof token !== 'string') {
      console.error('❌ Invalid token format. Expected string, got:', typeof token);
      throw new Error('Invalid token received from server');
    }
    
    console.log('✅ Token extracted successfully');
    
    // Return just the token string
    return token;
  } catch (error) {
    console.error('❌ Authentication error:', error);
    console.error('❌ Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || error.message || 'Authentication failed');
  }
};

/**
 * Step 3: Initiate onboarding
 * @param {string} parentClientId - The parent client ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response with parent_client_id and merchant_id
 */
export const initiateOnboarding = async (parentClientId, token) => {
  try {
    const client = createPocasClient(token);
    
    console.log('🚀 Initiating onboarding with parentClientId:', parentClientId);
    
    const payload = {
      parent_client_id: parentClientId,
    };
    
    console.log('📤 Initiate onboarding payload:', JSON.stringify(payload));
    
    const response = await client.post(API.onboardingInitiate(), payload);

    console.log('✅ Initiate onboarding response:', response.data);

    // Return the response data directly (contains parent_client_id and merchant_id)
    return response.data;
  } catch (error) {
    console.error('❌ Initiate onboarding error:', error);
    console.error('❌ Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || error.message || 'Failed to initiate onboarding');
  }
};

/**
 * Step 4: Update onboarding details
 * @param {Object} onboardingData - Complete onboarding data
 * @param {string} token
 * @returns {Promise<Object>} Update response
 */
export const updateOnboardingDetails = async (onboardingData, token) => {
  try {
    const client = createPocasClient(token);
    
    console.log('📤 Updating onboarding details:', JSON.stringify(onboardingData, null, 2));
    
    const response = await client.put(API.onboardingUpdate(), onboardingData);

    console.log('✅ Update onboarding response:', response.data);

    return response.data;
  } catch (error) {
    console.error('❌ Update onboarding error:', error);
    console.error('❌ Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || error.message || 'Failed to update onboarding details');
  }
};

/**
 * Step 5: Upload document
 * @param {string} merchantId
 * @param {string} documentType - e.g., 'pan', 'gst', 'aadhaar-front', 'aadhaar-back', 'cheque'
 * @param {File} file
 * @param {string} token
 * @returns {Promise<Object>} Upload response
 */
export const uploadDocument = async (merchantId, documentType, file, token) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    console.log(`📤 Uploading ${documentType} for merchant ${merchantId}...`);

    const response = await axios.post(
      API.onboardingUploadDocument(merchantId, documentType),
      formData,
      {
        headers: {
          'Authorization': token,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 seconds for file upload
      }
    );

    console.log(`✅ ${documentType} uploaded successfully`);

    return response.data;
  } catch (error) {
    console.error(`❌ Failed to upload ${documentType}:`, error);
    throw new Error(error.response?.data?.message || error.message || `Failed to upload ${documentType}`);
  }
};

/**
 * Step 5b: Upload brand logo (uses dedicated logo endpoint)
 * @param {string} merchantId
 * @param {File} file
 * @param {string} token
 * @returns {Promise<Object>} Upload response
 */
export const uploadBrandLogo = async (merchantId, file, token) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    console.log(`📤 Uploading brand logo for merchant ${merchantId}...`);

    const response = await axios.post(
      API.onboardingUploadLogo(merchantId),
      formData,
      {
        headers: {
          'Authorization': token,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
      }
    );

    console.log('✅ Brand logo uploaded successfully');

    return response.data;
  } catch (error) {
    console.error('❌ Failed to upload brand logo:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to upload brand logo');
  }
};

/**
 * Step 6: Submit onboarding for review
 * @param {string} merchantId
 * @param {string} token
 * @returns {Promise<Object>} Submit response
 */
export const submitOnboarding = async (merchantId, parentClientId, token) => {
  try {
    const client = createPocasClient(token);
    
    console.log('📤 Submitting onboarding for merchant:', merchantId);
    
    const response = await client.post(API.onboardingSubmit(), {
      merchant_id: merchantId,
      parent_client_id: parentClientId,
    });

    console.log('✅ Onboarding submitted successfully');

    return response.data;
  } catch (error) {
    console.error('❌ Submit onboarding error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to submit onboarding');
  }
};

/**
 * Step 7: Activate prepaid wallet
 * @param {string} merchantId
 * @param {string} token
 * @returns {Promise<Object>} Activation response
 */
export const activatePrepaidWallet = async (merchantId, token) => {
  try {
    const client = createOMSClient(token);
    
    console.log('📤 Activating prepaid wallet for merchant:', merchantId);
    
    const response = await client.put(API.walletActivatePrepaid(merchantId));

    console.log('✅ Prepaid wallet activated successfully');

    return response.data;
  } catch (error) {
    console.error('❌ Wallet activation error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to activate wallet');
  }
};

/**
 * Complete onboarding flow orchestrator
 * Handles the entire onboarding process step by step
 */
export const completeOnboardingFlow = async (formData, documents) => {
  try {
    // Step 1: Register user
    const registerResult = await registerUser(formData.registration);
    if (!registerResult.success) {
      throw new Error(registerResult.error);
    }

    // Step 2: Authenticate
    const authResult = await authenticateUser(
      formData.registration.username,
      formData.registration.password
    );
    if (!authResult.success) {
      throw new Error(authResult.error);
    }

    const { token, data: authData } = authResult;

    // Step 3: Initiate onboarding
    const initiateResult = await initiateOnboarding(authData.parentClientId, token);
    if (!initiateResult.success) {
      throw new Error(initiateResult.error);
    }

    const { merchantId } = initiateResult.data;

    // Step 4: Update onboarding details
    const onboardingData = {
      parent_client_id: authData.parentClientId,
      merchant_id: merchantId,
      basic_details: {
        email: formData.basicDetails.email,
        phone: formData.basicDetails.phone,
        name: formData.basicDetails.name,
        aadhaar_number: formData.basicDetails.aadhaar_number || '********',
        pan_number: formData.basicDetails.pan_number || '********',
        gst_number: formData.basicDetails.gst_number || '********',
        brand_name: formData.basicDetails.brand_name,
        email_verified: false,
        phone_verified: false,
      },
      address_details: {
        communication_address: formData.addressDetails,
      },
      wallet_type_preference: formData.walletType || 'POSTPAID',
    };

    // Add pickup details if provided
    if (formData.pickupDetails && formData.pickupDetails.length > 0) {
      onboardingData.pickup_details = formData.pickupDetails;
    }

    // Add bank details if provided
    if (formData.bankDetails && formData.bankDetails.account_number) {
      onboardingData.bank_details = formData.bankDetails;
    }

    const updateResult = await updateOnboardingDetails(onboardingData, token);
    if (!updateResult.success) {
      throw new Error(updateResult.error);
    }

    // Step 5: Upload documents
    const uploadResults = [];
    for (const [docType, file] of Object.entries(documents)) {
      if (file) {
        const uploadResult = await uploadDocument(merchantId, docType, file, token);
        uploadResults.push(uploadResult);
        if (!uploadResult.success) {
          console.warn(`Failed to upload ${docType}:`, uploadResult.error);
        }
      }
    }

    // Step 6: Submit onboarding
    const submitResult = await submitOnboarding(merchantId, authData.parentClientId, token);
    if (!submitResult.success) {
      throw new Error(submitResult.error);
    }

    // Step 7: Activate prepaid wallet
    const walletResult = await activatePrepaidWallet(merchantId, token);
    if (!walletResult.success) {
      console.warn('Wallet activation failed:', walletResult.error);
    }

    return {
      success: true,
      token,
      merchantId,
      parentClientId: authData.parentClientId,
      uploadResults,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Onboarding process failed',
    };
  }
};

export default {
  registerUser,
  authenticateUser,
  initiateOnboarding,
  updateOnboardingDetails,
  uploadDocument,
  uploadBrandLogo,
  submitOnboarding,
  activatePrepaidWallet,
  completeOnboardingFlow,
};


